import React, { useEffect, useMemo, useState } from 'react';
import { X, AlertTriangle, Repeat, ListOrdered, CalendarCheck, XCircle } from 'lucide-react';
import apiService from '../services/api';
import type { Appointment, AppointmentSeriesSummary } from '../types';

interface CustomerCancellationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (cancellationData: { noShow: boolean; cancellationNotes: string; cancelScope?: 'THIS' | 'REMAINING' }) => void;
  appointmentDetails: string;
  appointment?: Appointment | any;
  isLoading?: boolean;
}

const CustomerCancellationModal: React.FC<CustomerCancellationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  appointmentDetails,
  appointment,
  isLoading = false
}) => {
  const [cancellationNotes, setCancellationNotes] = useState('');
  const [cancelScope, setCancelScope] = useState<'THIS' | 'REMAINING'>('THIS');
  const [seriesSummary, setSeriesSummary] = useState<AppointmentSeriesSummary | null>(null);
  const [isSeriesLoading, setIsSeriesLoading] = useState(false);

  const recurringMeta = useMemo(() => {
    const seriesId = appointment?.seriesId ?? appointment?.series_id ?? null;
    const occurrenceIndexRaw = appointment?.occurrenceIndex ?? appointment?.occurrence_index ?? null;
    const seriesOccurrenceCountRaw = appointment?.seriesOccurrenceCount ?? appointment?.series_occurrence_count ?? null;

    const occurrenceIndex = occurrenceIndexRaw != null ? Number(occurrenceIndexRaw) : null;
    const seriesOccurrenceCount = seriesOccurrenceCountRaw != null ? Number(seriesOccurrenceCountRaw) : null;

    return {
      seriesId: seriesId ? String(seriesId) : null,
      occurrenceIndex: Number.isFinite(occurrenceIndex as any) ? (occurrenceIndex as number) : null,
      seriesOccurrenceCount: Number.isFinite(seriesOccurrenceCount as any) ? (seriesOccurrenceCount as number) : null
    };
  }, [appointment]);

  useEffect(() => {
    let cancelled = false;
    if (!isOpen) return;

    // Reset UI state on open (but keep notes until close)
    setCancelScope('THIS');
    setSeriesSummary(null);

    const appointmentId = appointment?.id;
    if (!appointmentId || !recurringMeta.seriesId) return;

    setIsSeriesLoading(true);
    apiService
      .getAppointmentSeriesSummary(String(appointmentId))
      .then((data) => {
        if (cancelled) return;
        setSeriesSummary(data);
      })
      .catch(() => {
        if (cancelled) return;
        setSeriesSummary(null);
      })
      .finally(() => {
        if (cancelled) return;
        setIsSeriesLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, appointment?.id, recurringMeta.seriesId]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onConfirm({ noShow: false, cancellationNotes, cancelScope: recurringMeta.seriesId ? cancelScope : 'THIS' });
  };

  const handleClose = () => {
    setCancellationNotes('');
    setCancelScope('THIS');
    setSeriesSummary(null);
    onClose();
  };

  if (!isOpen) return null;

  const remainingAfterThisFallback =
    recurringMeta.occurrenceIndex && recurringMeta.seriesOccurrenceCount
      ? Math.max(recurringMeta.seriesOccurrenceCount - recurringMeta.occurrenceIndex, 0)
      : 0;

  const remainingActiveAfterThis =
    typeof seriesSummary?.remainingActiveAfterThis === 'number'
      ? seriesSummary.remainingActiveAfterThis
      : remainingAfterThisFallback;

  const showCancelScopeChoice =
    Boolean(recurringMeta.seriesId) &&
    Number.isFinite(remainingActiveAfterThis) &&
    remainingActiveAfterThis > 0;

  const completedLabel = (() => {
    if (typeof seriesSummary?.completedCount === 'number') return seriesSummary.completedCount;
    if (recurringMeta.occurrenceIndex && recurringMeta.occurrenceIndex > 0) return Math.max(recurringMeta.occurrenceIndex - 1, 0);
    return null;
  })();

  const remainingLabel = (() => {
    if (typeof seriesSummary?.remainingActiveIncludingThis === 'number') return seriesSummary.remainingActiveIncludingThis;
    if (recurringMeta.occurrenceIndex && recurringMeta.seriesOccurrenceCount) {
      return Math.max(recurringMeta.seriesOccurrenceCount - recurringMeta.occurrenceIndex + 1, 1);
    }
    return null;
  })();

  return (
    <div
      className="fixed inset-0 z-[2000] flex items-start sm:items-center justify-center bg-black/60 backdrop-blur-sm p-4 pt-20 sm:pt-4"
      style={{ zIndex: 2000 }}
    >
      {/* Modal content */}
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md relative border border-gray-200 overflow-hidden max-h-[calc(100vh-6rem)] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 rounded-t-lg shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="p-2 bg-red-100 rounded-lg mr-3">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Cancel Appointment</h3>
            </div>
            <button 
              onClick={handleClose} 
              className="text-gray-400 hover:text-gray-600 p-1 rounded-md hover:bg-gray-100"
              aria-label="Close"
              disabled={isLoading}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 flex-1 flex flex-col min-h-0">
          <div className="flex-1 min-h-0 overflow-y-auto pr-1 space-y-5">
            <p className="text-sm text-gray-600 mb-4">
              Are you sure you want to cancel this appointment? This action cannot be undone.
            </p>
            
            {/* Appointment Details */}
            <div className="bg-gray-50 border border-gray-200 rounded-md p-4 mb-4">
              <p className="text-sm font-medium text-gray-900">{appointmentDetails}</p>
            </div>

            {/* Recurring series info + cancel scope (only when applicable) */}
            {recurringMeta.seriesId && (
              <div className="mb-4 border border-blue-100 bg-blue-50/40 rounded-md p-4">
                <div className="flex items-start">
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-gray-900">This appointment is part of a series</p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {recurringMeta.occurrenceIndex && recurringMeta.seriesOccurrenceCount ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-blue-200 bg-white text-xs font-semibold text-blue-900">
                          <ListOrdered className="w-3.5 h-3.5 text-blue-700" aria-hidden />
                          <span>
                            {recurringMeta.occurrenceIndex} of {recurringMeta.seriesOccurrenceCount}
                          </span>
                        </span>
                      ) : null}
                      {completedLabel != null ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-gray-200 bg-white text-xs font-semibold text-gray-700">
                          <span>Completed: {completedLabel}</span>
                        </span>
                      ) : null}
                      {remainingLabel != null ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-gray-200 bg-white text-xs font-semibold text-gray-700">
                          <span>Remaining: {remainingLabel}</span>
                        </span>
                      ) : null}
                    </div>

                    {isSeriesLoading ? (
                      <p className="mt-2 text-xs text-gray-600">Checking remaining appointments in this series…</p>
                    ) : null}

                    {showCancelScopeChoice ? (
                      <div className="mt-3">
                        <p className="text-xs font-medium text-gray-700 mb-2">What would you like to cancel?</p>
                        <div className="space-y-2">
                          <label className="flex items-start gap-3 p-3 rounded-md border border-gray-200 bg-white hover:bg-gray-50 cursor-pointer">
                            <input
                              type="radio"
                              name="cancelScope"
                              value="THIS"
                              checked={cancelScope === 'THIS'}
                              onChange={() => setCancelScope('THIS')}
                              disabled={isLoading}
                              className="mt-1"
                            />
                            <div>
                              <p className="text-sm font-semibold text-gray-900">Cancel only this appointment</p>
                              <p className="text-xs text-gray-600">Your other upcoming appointments in this series will stay scheduled.</p>
                            </div>
                          </label>
                          <label className="flex items-start gap-3 p-3 rounded-md border border-red-200 bg-white hover:bg-red-50 cursor-pointer">
                            <input
                              type="radio"
                              name="cancelScope"
                              value="REMAINING"
                              checked={cancelScope === 'REMAINING'}
                              onChange={() => setCancelScope('REMAINING')}
                              disabled={isLoading}
                              className="mt-1"
                            />
                            <div>
                              <p className="text-sm font-semibold text-gray-900">
                                Cancel this and all remaining appointments
                              </p>
                              <p className="text-xs text-gray-600">
                                This will also cancel {remainingActiveAfterThis} upcoming appointment{remainingActiveAfterThis === 1 ? '' : 's'} in this series.
                              </p>
                            </div>
                          </label>
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            )}

            {/* Cancellation Notes */}
            <div>
              <label htmlFor="cancellationNotes" className="block text-sm font-medium text-gray-700 mb-2">
                Cancellation Reason
                <span className="ml-2 text-xs font-normal text-gray-500">(Optional)</span>
              </label>
              <textarea
                id="cancellationNotes"
                value={cancellationNotes}
                onChange={(e) => setCancellationNotes(e.target.value)}
                rows={4}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white text-gray-900 placeholder-gray-400 resize-none"
                placeholder="Please let us know why you need to cancel this appointment..."
                disabled={isLoading}
                maxLength={500}
              />
              <p className="mt-2 text-xs text-gray-500">
                {cancellationNotes.length}/500 characters
              </p>
            </div>
          </div>

          {/* Footer Actions */}
          <div className="flex w-full justify-stretch sm:justify-end gap-3 pt-4 border-t border-gray-200 shrink-0">
            <button
              type="button"
              onClick={handleClose}
              className="inline-flex flex-1 sm:flex-none justify-center items-center px-4 py-2.5 text-sm font-medium rounded-md border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              disabled={isLoading}
            >
              <CalendarCheck className="w-4 h-4 mr-2" aria-hidden />
              <span className="sm:hidden">Keep</span>
              <span className="hidden sm:inline">Keep Appointment</span>
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className={`inline-flex flex-1 sm:flex-none justify-center items-center px-4 py-2.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 ${
                isLoading ? 'opacity-70 cursor-not-allowed' : ''
              }`}
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Cancelling...
                </>
              ) : (
                <>
                  <XCircle className="w-4 h-4 mr-2" aria-hidden />
                  <span className="sm:hidden">I'd like to Cancel</span>
                  <span className="hidden sm:inline">Cancel Appointment</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CustomerCancellationModal; 